import React, { useEffect, useState } from 'react';
import { tt } from '@i18n/tt';
import { useNavigate } from 'react-router-dom';
import { Avatar, Chip, FormControl, Grid, MenuItem, Select, Typography } from '@mui/material';

import { TICKET } from 'router/routes';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { ThunkGetChats } from 'store/chats/thunks';
import { ChatsSelector } from 'store/chats/selectors';

import { getFormattedDate } from 'utils/dates';
import { readMessage } from '../../../services/chats';

import Loader from 'components/Loader';

import useStyles from '../styles';
import CustomTypography from '../../../components/CustomTypography';
import { removeNotification } from '../../../store/notifications';

const UserChats: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState<number>(() => {
    const raw = localStorage.getItem('clientPortal.recentMessages.days');
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : 7;
  });

  const classes = useStyles();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const chats = useAppSelector(ChatsSelector);

  useEffect(() => {
    setLoading(true);
    dispatch(ThunkGetChats({ search: '', days }))
      .unwrap()
      .finally(() => {
        setLoading(false);
      });
  }, [dispatch, days]);

  return (
    <div className={classes.ticketsContainer}>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className={classes.title}>
            <CustomTypography isDarkTextColor fontSize="20px" fontWeight={700}>
              {tt('pages.home.chats.title')}
            </CustomTypography>
            <FormControl size="small" className={classes.recentMessagesRangeControl}>
              <Select
                value={days}
                onChange={event => {
                  const nextDays = Number(event.target.value);
                  setDays(nextDays);
                  localStorage.setItem('clientPortal.recentMessages.days', String(nextDays));
                }}
                className={classes.recentMessagesRangeSelect}
              >
                {[7, 14, 30, 60, 90].map(optionDays => (
                  <MenuItem key={optionDays} value={optionDays}>
                    {tt('pages.home.chats.range.daysOption', { days: optionDays })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className={classes.chatsList}>
            {!chats.length ? (
              <Typography className={classes.recentMessagesEmptyState}>
                {tt('pages.home.chats.range.emptyState', { days })}
              </Typography>
            ) : (
              [...chats]
              .sort((a, b) => {
                const dateA = a?.lastMessage?.dateTime ? new Date(a.lastMessage.dateTime).getTime() : 0;
                const dateB = b?.lastMessage?.dateTime ? new Date(b.lastMessage.dateTime).getTime() : 0;
                return dateB - dateA;
              })
              .map(chat => {
                const isFromUser = !!chat.lastMessage?.contactName;
                const senderName = isFromUser
                  ? 'You'
                  : chat.lastMessage?.resourceName ?? chat.techName ?? 'Support';
                const avatarSrc = isFromUser ? undefined : chat.techProfileImg;
                const hasUnread = chat.unreadCount > 0;

                return (
                  <div
                    key={chat.id}
                    onClick={() => {
                      readMessage(chat.ticketId).then(() => {
                        dispatch(removeNotification(chat.id));
                        navigate(TICKET.replace(':ticketId', chat.ticketId));
                      });
                    }}
                    className={classes.chatItem}
                  >
                    <Avatar src={avatarSrc} className={classes.chatAvatar} />
                    <div className={classes.chatContent}>
                      <div className={classes.chatHeader}>
                        <Grid display="flex" gap={1} alignItems="center">
                          <Typography
                            variant="body1"
                            className={classes.chatSenderName}
                            fontSize={16}
                          >
                            {senderName}
                          </Typography>
                          {hasUnread && (
                            <Chip
                              className={classes.chatChip}
                              label={tt('pages.home.chats.unreadMessages', {
                                count: chat.unreadCount,
                                plural: chat.unreadCount === 1 ? 'message' : 'messages',
                              })}
                            />
                          )}
                        </Grid>

                        <Typography variant="body2" className={classes.chatTime}>
                          {getFormattedDate(chat.lastMessage?.dateTime ?? '', 'MM/DD/YYYY hh:mm A')}
                        </Typography>
                      </div>
                      <div className={classes.messageWrapper}>
                        <Typography
                          variant="body2"
                          className={classes.chatMessagePreview}
                          noWrap
                          fontSize={12}
                        >
                          <span className={classes.lastMessageLabel}>
                            {tt('pages.home.chats.lastMessage')}:{' '}
                          </span>
                          {chat.lastMessage?.message ?? ''}
                        </Typography>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UserChats;
